import { PaymentStatus } from "@repo/database";
import { OrderSettingsSchema } from "@repo/validators";
import { Users } from "lucide-react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";
import { useShallow } from "zustand/react/shallow";

import type { Order } from "~/providers/order-provider";
import { useLabel } from "~/hooks/use-label";
import { useOrderStore } from "~/stores/use-order-store";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  useForm,
} from "../ui/form";
import { Input } from "../ui/input";
import { ModalInput } from "../ui/modal-input";
import { MultiSelect } from "../ui/multi-select";

export default function OrderSettings({
  order,
  onClose,
}: {
  order?: Order;
  onClose: () => void;
}) {
  const { t } = useTranslation("common");
  const { setCurrentOrder } = useOrderStore(
    useShallow((state) => ({
      setCurrentOrder: state.setCurrentOrder,
    })),
  );

  const { labels } = useLabel();

  const labelOptions = labels.map((label) => ({
    label: label.text,
    value: label.id,
  }));

  const form = useForm({
    schema: OrderSettingsSchema,
    defaultValues: {
      name: order?.name ?? "",
      partySize: order?.partySize ?? undefined,
      bookingId: order?.bookingId,
      labelIds: order?.labelIds ?? [],
    },
  });

  const handleSubmit = form.handleSubmit((formData) => {
    setCurrentOrder({
      id: order?.id ?? uuidv4(),
      items: order?.items ?? [],
      currentRound: 1,
      tableId: order?.tableId,
      updatedAt: order?.updatedAt ?? new Date(),
      createdAt: order?.createdAt ?? new Date(),
      paymentStatus: order?.paymentStatus ?? PaymentStatus.pending,
      labelIds: formData.labelIds,
      bookingId: order?.bookingId ?? undefined,
      name: formData.name,
      partySize: formData.partySize,
      total: order?.total ?? 0,
      amountPaid: order?.amountPaid ?? 0,
      creditId: order?.creditId ?? undefined,
      payments: order?.payments ?? [],
      ticketPrinted: order?.ticketPrinted ?? false,
    });
    onClose();
  });

  return (
    <Form {...form}>
      <form
        onSubmit={handleSubmit}
        id="order-settings"
        className="grid grid-cols-2 gap-2"
      >
        <FormField
          control={form.control}
          name="labelIds"
          render={({ field }) => (
            <FormItem className="col-span-full">
              <FormLabel>{t("Order.labels")}</FormLabel>
              <FormControl>
                <MultiSelect
                  placeholder="Select labels..."
                  onValueChange={(value) => {
                    field.onChange(value);
                  }}
                  options={labelOptions.sort((a, b) =>
                    a.label.localeCompare(b.label),
                  )}
                  defaultValue={field.value}
                  value={field.value}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem className="col-span-full">
              <FormLabel>{t("Order.reference")}</FormLabel>
              <FormControl>
                <Input {...field} placeholder={t("Order.reference")} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="partySize"
          render={({ field }) => (
            <FormItem className="col-span-full">
              <FormLabel>{t("Order.party_size")}</FormLabel>
              <FormControl>
                <ModalInput
                  icon={<Users className="text-white" />}
                  label={t("Order.party_size")}
                  description={t("Order.party_size_description")}
                  defaultOpen={false}
                  type="number"
                  min={1}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        {/* <FormField
          control={form.control}
          name="bookingId"
          render={({ field }) => (
            <FormItem className="col-span-full">
              <FormLabel>{t("Order.booking")}</FormLabel>
              <Dialog>
                <DialogTrigger asChild>
                  <Button variant="outline" className="w-full">
                    ...
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{t("Order.booking")}</DialogTitle>
                    <VisuallyHidden>
                      <DialogDescription></DialogDescription>
                    </VisuallyHidden>
                  </DialogHeader>
                  <ScrollArea className="max-h-[80vh] p-4">
                    <div>woel</div>
                  </ScrollArea>
                </DialogContent>
              </Dialog>
              <FormMessage />
            </FormItem>
          )}
        /> */}
      </form>
    </Form>
  );
}
